import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'

import { types, initialState } from './types'
import { updateExpenseReceipts, deleteExpenseReceipt } from './utils'
import { fetchActiveExpense } from './Expense'

import { replaceOrCreateObjectInArray } from '../utils/replaceOrCreateObjectInArray'

export const submitReceipts = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath(`exp`, `expenses/${expenseUuid}/submit`),
    method: 'PUT',
    types: [
      types.SUBMIT_RECEIPTS_REQUEST,
      types.SUBMIT_RECEIPTS_SUCCESS,
      types.SUBMIT_RECEIPTS_ERROR,
    ],
  },
})

export const uploadReceipt = (file, expenseUuid) => {
  const formData = new FormData()
  formData.append('file', file)
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `expenses/receipts/upload${makeQueryParams({
          memberUuid: webStorage.getItem('sub'),
          expenseUuid,
        })}`,
      ),
      method: 'POST',
      headers: {
        'Content-Type': 'IGNORE',
      },
      body: formData,
      types: [
        types.UPLOAD_RECEIPT_REQUEST,
        {
          type: types.UPLOAD_RECEIPT_SUCCESS,
          meta: { expenseUuid },
        },
        types.UPLOAD_RECEIPT_ERROR,
      ],
    },
  }
}

export const updateUploadReceiptWarnings = warnings => {
  return {
    payload: warnings,
    type: types.UPDATE_UPLOAD_RECEIPT_WARNINGS,
  }
}

export const clearUploadReceiptWarning = warningName => ({
  payload: warningName,
  type: types.CLEAR_UPLOAD_RECEIPT_WARNING,
})

export const clearUploadReceiptWarnings = () => ({
  type: types.CLEAR_UPLOAD_RECEIPT_WARNINGS,
})

export const deleteReceipt = ({ expenseUuid, receiptUuid }) => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `receipts/${receiptUuid}`),

    method: 'DELETE',
    types: [
      types.DELETE_RECEIPT_REQUEST,
      {
        type: types.DELETE_RECEIPT_SUCCESS,
        meta: { expenseUuid, receiptUuid },
      },
      types.DELETE_RECEIPT_ERROR,
    ],
  },
})

export const editReceipt = ({ expenseUuid, name, receiptUuid }) => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `receipts/${receiptUuid}`),
    body: JSON.stringify({
      name,
    }),
    method: 'PUT',
    types: [
      types.EDIT_RECEIPT_REQUEST,
      {
        type: types.EDIT_RECEIPT_SUCCESS,
        meta: { expenseUuid },
      },
      types.EDIT_RECEIPT_ERROR,
    ],
  },
})

export const evaluateReceipt = (endpoint, body) => ({
  [RSAA]: {
    endpoint: endpoint,
    method: 'POST',
    body: body,
    types: [
      types.EVALUATE_RECEIPT_REQUEST,
      types.EVALUATE_RECEIPT_SUCCESS,
      types.EVALUATE_RECEIPT_ERROR,
    ],
  },
})

export const fetchReceipts = expenseUuid => ({
  // At the moment, BE does not have a "fetch receipts" endpoint.
  // But it's needed to prevent a full page reload.
  // So we're going to call the eob endpoint like fetchActiveExpense
  // but just only update the receipts within redux and ignore
  // the rest of the BE response.
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}/eob`),
    method: 'GET',
    types: [
      {
        type: types.FETCH_RECEIPTS_REQUEST,
        meta: { uuid: expenseUuid },
      },
      types.FETCH_RECEIPTS_SUCCESS,
      {
        type: types.FETCH_RECEIPTS_ERROR,
        meta: { uuid: expenseUuid },
      },
    ],
  },
})

export const clearReceiptError = () => ({
  type: types.CLEAR_RECEIPT_ERRORS,
})

// Kept aligned with SubmitReceiptButton's environmentEndpoints map. Duplicated
// intentionally to avoid pulling a store module into that component just for a
// URL table; if a third caller shows up, hoist to a shared constants file.
const EVALUATE_SUBMISSION_ENDPOINTS = {
  prod: 'https://prod-api-gateway.sidecarhealth.com/evaluate-submission',
  qa: 'https://qa-api-gateway.sidecarhealth.com/evaluate-submission',
  dev: 'https://dev-api-gateway.sidecarhealth.com/evaluate-submission',
}

const getEvaluateSubmissionEndpoint = () =>
  EVALUATE_SUBMISSION_ENDPOINTS[process.env.APP_ENV] ||
  EVALUATE_SUBMISSION_ENDPOINTS.dev

/**
 * Submit an expense whose proof is a _link_, not an uploaded document, through
 * the evaluate-submission gateway (the same endpoint SubmitReceiptButton uses).
 * No build-a-bill / payment-questions / clinical-notes fields apply on either
 * link-only path. Callers dispatch this directly with the args for their path:
 *
 * - **Swipe-link reopen** (Processed page): `{ expenseUuid, memberReopenReason:
 *   MEMBER_REOPEN_REASONS.BALANCE_STILL_DUE }` — posts `receipt_filenames: []`
 *   and reads member DOB from the Member slice the way
 *   SubmitReceiptButton.handleSubmitClick does; the reason tells the BE to
 *   reopen the closed expense the way the manual "Submit proof" CTA does. Goes
 *   through evaluate-submission, NOT `submitReceipts` (`PUT /submit`), which
 *   only serves CorrectionsNeeded's "Submit anyway".
 * - **SmithRx fill link** (fill picker, G20-3094): `{ expenseUuid, fillUuids }` —
 *   the confirmed fills ARE the proof; posts `fill_uuids` (no
 *   `receipt_filenames`) and the submission itself links them, returning
 *   `COMPLETED` (sending them is what avoids the earlier `FAILED`); no
 *   `/fills/match` call.
 *
 * @param {object} params
 * @param {string} params.expenseUuid
 * @param {string} [params.memberReopenReason] - Set on the reopen path so the
 *   BE reopens a closed expense; omitted for a fill-link submission.
 * @param {string[]} [params.fillUuids] - SmithRx fills the member selected;
 *   present only on the fill-link path. Passed as `fill_uuids` so the
 *   submission links them.
 * @param {number} [params.nonPrescriptionAmountCents] - G20-3110: the
 *   swipe-minus-fills remainder in integer cents, sent on the fill-link path
 *   only when the member confirmed a non-prescription item accounts for the
 *   gap. Omitted otherwise.
 * @returns Thunk resolving to the evaluateReceipt FSA. On success, also
 *   dispatches fetchActiveExpense so the UI reflects the new status.
 */
export const submitLinkedExpense =
  ({
    expenseUuid,
    memberReopenReason,
    fillUuids,
    nonPrescriptionAmountCents,
  }) =>
  async (dispatch, getState) => {
    const state = getState()
    const activeExpense = state.Expense?.activeExpense?.entity || {}
    const memberUuid = activeExpense.memberUuid
    const member = state.Member?.members?.entities?.[memberUuid] || {}

    const isFillLink = Array.isArray(fillUuids)

    // The fill-link submission substantiates the expense with `fill_uuids`; the
    // reopen path carries the legacy receipt/build-a-bill shape instead.
    const body = JSON.stringify(
      isFillLink
        ? {
            fill_uuids: fillUuids,
            expense_uuid: expenseUuid,
            member_uuid: memberUuid,
            has_member_feedback: false,
            // TODO(G20-3110): field name is PROVISIONAL — pending Connor's BE
            // contract for the non-prescription remainder. Rename to the agreed
            // key once the evaluate-submission payload is finalized.
            ...(nonPrescriptionAmountCents != null && {
              nonPrescriptionAmountCents,
            }),
          }
        : {
            receipt_filenames: [],
            expense_uuid: expenseUuid,
            member_uuid: memberUuid,
            member_dob: member.dateOfBirth,
            expenseType: activeExpense.expenseType,
            buildABill: false,
            ...(memberReopenReason && { memberReopenReason }),
          },
    )

    const result = await dispatch(
      evaluateReceipt(getEvaluateSubmissionEndpoint(), body),
    )
    if (!result?.error) {
      await dispatch(fetchActiveExpense(expenseUuid))
    }
    return result
  }

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  const activeExpense = state.activeExpense?.entity
  const searchResults = state.searchResults?.entries

  switch (type) {
    case types.UPLOAD_RECEIPT_REQUEST:
    case types.DELETE_RECEIPT_REQUEST:
    case types.SUBMIT_RECEIPTS_REQUEST:
    case types.EDIT_RECEIPT_REQUEST: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          busy: true,
          error: null,
        },
      }
    }
    case types.EVALUATE_RECEIPT_REQUEST: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          evaluatingReceiptsBusy: true,
          busy: true,
          error: null,
        },
      }
    }

    case types.SUBMIT_RECEIPTS_SUCCESS:
      return {
        ...state,
        receipts: {
          ...state.receipts,
          busy: false,
        },
        searchResults: {
          ...state.searchResults,
          entries: replaceOrCreateObjectInArray({
            array: state.searchResults.entries,
            itemKey: 'uuid',
            targetValue: meta.uuid,
            newElement: payload,
          }),
        },
      }

    case types.UPLOAD_RECEIPT_SUCCESS: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          busy: false,
        },
      }
    }

    case types.EVALUATE_RECEIPT_SUCCESS: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          evaluatingReceiptsBusy: false,
          busy: false,
        },
      }
    }

    case types.DELETE_RECEIPT_SUCCESS: {
      // Step 1: First update the activeExpense if it exists
      // The activeExpense is what the user is currently viewing
      // If no active expense entity, keep as is
      const updatedActiveExpense = state.activeExpense?.entity
        ? {
            ...state.activeExpense,
            entity: deleteExpenseReceipt(
              state.activeExpense.entity,
              meta.receiptUuid,
            ),
          }
        : state.activeExpense

      // Step 2: Defensive check - if searchResults.entries isn't valid, just update activeExpense
      // This prevents "findIndex is not a function" errors when the data structure is unexpected
      if (
        !state.searchResults?.entries ||
        !Array.isArray(state.searchResults.entries)
      ) {
        return {
          ...state,
          receipts: {
            ...state.receipts,
            busy: false,
          },
          activeExpense: updatedActiveExpense,
        }
      }

      // Step 3: Find the specific expense in the search results that needs updating
      // We check both uuid and expenseUuid fields to be safe
      const index = state.searchResults.entries.findIndex(
        obj =>
          obj.uuid === meta.expenseUuid || obj.expenseUuid === meta.expenseUuid,
      )

      // Step 4: If expense not found in search results, just update the activeExpense
      // This can happen if the expense was deleted or filtered out of search results
      if (index === -1) {
        return {
          ...state,
          receipts: {
            ...state.receipts,
            busy: false,
          },
          activeExpense: updatedActiveExpense,
        }
      }

      // Step 5: Create a new entries array with the updated expense
      // We need to create a new array to maintain Redux immutability principles
      const newEntries = [...state.searchResults.entries]

      // Update the specific expense in our search results by removing the receipt
      newEntries[index] = deleteExpenseReceipt(
        state.searchResults.entries[index],
        meta.receiptUuid,
      )

      // Step 6: Return the fully updated state with both activeExpense and searchResults
      // This ensures the UI is consistent after deleting a receipt
      return {
        ...state,
        receipts: {
          ...state.receipts,
          busy: false,
        },
        activeExpense: updatedActiveExpense,
        searchResults: {
          ...state.searchResults,
          entries: newEntries,
        },
      }
    }

    case types.EDIT_RECEIPT_SUCCESS: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          busy: false,
        },
        activeExpense: {
          ...state.activeExpense,
          entity: updateExpenseReceipts(activeExpense, payload),
        },
        searchResults: {
          ...state.searchResults,
          entries: updateExpenseReceipts(
            searchResults[meta.expenseUuid],
            payload,
          ),
        },
      }
    }

    case types.UPLOAD_RECEIPT_ERROR:
    case types.EDIT_RECEIPT_ERROR:
    case types.SUBMIT_RECEIPTS_ERROR:
    case types.EVALUATE_RECEIPT_ERROR:
    case types.DELETE_RECEIPT_ERROR: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          busy: false,
          evaluatingReceiptsBusy: false,
          error: payload.response.message,
        },
      }
    }

    case types.UPDATE_UPLOAD_RECEIPT_WARNINGS: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          warnings: { ...state.receipts.warnings, ...payload },
        },
      }
    }

    case types.FETCH_RECEIPTS_REQUEST: {
      return {
        ...state,
        // do not edit busy because that's the main problem the page refreshes
      }
    }
    case types.FETCH_RECEIPTS_SUCCESS: {
      return {
        ...state,
        activeExpense: {
          ...state.activeExpense,
          entity: {
            ...state.activeExpense.entity,
            documents: payload.documents,
            // we also need to update the id because it can change from
            // a temp id to a real id on upload. that then updates the page title.
            expenseId: payload.expenseId,
            // we also need to update expense status because it can change
            // from "corrections needed" to "pending submission" on upload
            expenseStatus: payload.expenseStatus,
          },
          error: null,
          busy: false,
        },
      }
    }
    case types.FETCH_RECEIPTS_ERROR: {
      return {
        ...state,
        activeExpense: {
          ...state.activeExpense,
          error: payload.response.message,
          busy: false,
        },
      }
    }

    case types.CLEAR_UPLOAD_RECEIPT_WARNING: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          warnings: {
            ...state.receipts.warnings,
            [payload]: null,
          },
        },
      }
    }

    case types.CLEAR_UPLOAD_RECEIPT_WARNINGS: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          warnings: {},
        },
      }
    }

    case types.CLEAR_RECEIPT_ERRORS: {
      return {
        ...state,
        receipts: {
          ...state.receipts,
          error: '',
        },
      }
    }

    default:
      return state
  }
}
